import { PurchaseData } from "../../types";

export interface AvailableWalletMethods {
  applePay: boolean;
  googlePay: boolean;
}

export const NO_WALLET_METHODS: AvailableWalletMethods = {
  applePay: false,
  googlePay: false,
};

export type WalletBlockReason =
  | "refund_protection"
  | "terms_and_conditions"
  | "basket_updating"
  | "shipping_address";

/**
 * Shared props across PaymentGateway Wallet elements
 */
export type WalletSourceStep =
  | "payment_step"
  | "detail_page_modal"
  | "enhanced_cart_summary"
  | "enhanced_cart_summary_attraction"
  | "enhanced_cart_summary_attraction_footer";

export type WalletElementSharedProps = {
  /**
   * Called when a payment processor's element is deemed ready
   */
  onElementReady?: () => void;
  /**
   * Called when a payment processor's element has an error
   */
  onElementError?: () => void;
  /**
   * Called when the Wallet component itself is being unmounted.
   * Note the distinction between a processor's element and a react component.
   */
  onComponentUnmount?: () => void;
  /**
   * Called when the Wallet payment is successful.
   */
  onPaymentSuccess?: (purchaseData: PurchaseData) => void;
  /**
   * Should be in the format of ISO 3166-1 short names (common names)
   * e.g. ["United States", "Canada"]
   * When not provided, all countries will be allowed.
   */
  allowedShippingCountries?: string[];
  /**
   * When non-empty, an invisible overlay intercepts all clicks on the wallet
   * buttons, preventing the payment flow from starting.
   * Each entry describes why the wallet is blocked (e.g. missing refund
   * protection selection, terms not accepted).
   */
  blockReasons?: WalletBlockReason[];
  /**
   * Called when the user clicks while the wallet is blocked.
   * Use this to show validation errors or toasts.
   */
  onClickBlocked?: () => void;

  /**
   * Called when the user clicks the wallet element.
   */
  onWalletClick?: () => void;

  /**
   * Called after the user cancels/dismisses the wallet payment sheet
   * and basket restoration completes.
   */
  onWalletCancel?: () => void;

  /**
   * Called when the payment gateway reports which wallet methods are
   * available (e.g. Apple Pay, Google Pay). Fired alongside onElementReady
   * so consumers can act on the concrete set of available wallets.
   */
  onAvailablePaymentMethods?: (methods: AvailableWalletMethods) => void;
};
